/**
 * Abrechnung Stufe 1 — reine Anzeige-Regeln für den Reiter „Abrechnen“. Beträge kommen vom Server;
 * die einzige Rechnung hier ist die VORSCHAU in den Drawern (Rabatt, Mehrverbrauch, Getränkeanteil),
 * und die läuft über dieselbe Funktion wie der Server (`@maestro/pricing`), nie über eigene Formeln.
 * Gespeichert und abgerechnet wird ausschließlich, was der Server daraus rechnet.
 */
import { berechneAbrechnung, teileSpeisenGetraenke, vatFromGross, AbrechnungsFehler, type AbrechnungsErgebnis, type AbrechnungsZeile } from "@maestro/pricing";
import { money } from "../ui/format";
import type { AbrZeile, AbrZeileEingabe, AbrAntwort } from "../ui/abrechnung/typen";

export const satzText = (bp: number | null | undefined) => (bp == null ? "–" : `${(bp / 100).toLocaleString("de-DE")} %`);
/** Basispunkte als Eingabetext eines Prozentfelds („5“, „2,5“) — reine Anzeige, kein Betrag. */
export const bpAlsEingabe = (bp: number) => (bp / 100).toLocaleString("de-DE", { maximumFractionDigits: 2 });
export const prozentText = (bp: number) => `${(bp / 100).toLocaleString("de-DE", { maximumFractionDigits: 2 })} %`;

const ZAHLWEISE: Record<string, { text: string; icon: string }> = {
  stripe: { text: "Stripe", icon: "link" },
  ec_karte: { text: "EC-Karte", icon: "credit_card" },
  ueberweisung: { text: "Überweisung", icon: "account_balance" },
  bar: { text: "Bar", icon: "payments" },
};
export const zahlweise = (z: string | null | undefined) => (z ? (ZAHLWEISE[z] ?? { text: z, icon: "payments" }) : { text: "Zahlung", icon: "payments" });

export const istBetragszeile = (z: Pick<AbrZeile, "art">) => z.art !== "rabatt" && z.art !== "zuschlag";

/** „100 × 42,00 € pro Person“, „2,5 × 9,99 €“, „Pauschal“. */
export function mengeText(z: AbrZeile, gaeste: number): string {
  const preis = money(z.einzelpreisCents ?? 0);
  const m = (z.mengeMilli ?? 1000) / 1000;
  if (z.proPerson) return `${(m * gaeste).toLocaleString("de-DE")} × ${preis}${m === 1 ? " pro Person" : ""}`;
  if (z.art !== "leistung" && m === 1) return z.art === "mehrverbrauch" && (z.einzelpreisCents ?? 0) < 0 ? "Minderverbrauch" : z.art === "gutschrift" ? "Gutschrift" : "Mehrverbrauch";
  if (m === 1 && z.einheit === "Pauschal") return "Pauschal";
  return `${m.toLocaleString("de-DE")} × ${preis}${z.einheit && z.einheit !== "Pauschal" ? ` · ${z.einheit}` : ""}`;
}

/** Unterzeile einer Rabatt-/Zuschlagszeile: Bezug, Basis und Aufteilung je Satz (Konzept § 7: jede Rabattzeile zeigt ihre Basis). */
export function rabattUnterzeile(z: AbrZeile, e: AbrechnungsErgebnis["zeilen"][number] | undefined, alle: readonly AbrZeile[] = []): string {
  const bezug =
    z.bezug === "zwischensumme"
      ? "auf Zwischensumme bis hier"
      : z.bezug === "zeilen"
        ? `auf ${(z.bezugZeilen ?? []).map((id) => alle.find((x) => x.id === id)).filter((x): x is AbrZeile => Boolean(x)).map(zeilenTitel).join(", ") || "keine Zeile"}`
        : "auf Leistungen darüber";
  if (!e) return bezug;
  const teile = e.jeSatz.map((s) => `${money(Math.abs(s.cents))} zu ${satzText(s.rateBp)}`).join(", ");
  return `${bezug} · Basis ${money(e.basisCents ?? 0)}${teile ? ` · ${teile}` : ""}`;
}

export function zeilenTitel(z: AbrZeile): string {
  if (z.art === "rabatt" || z.art === "zuschlag") {
    const art = z.art === "rabatt" ? "Rabatt" : "Zuschlag";
    return z.prozentBp ? `${art} ${prozentText(z.prozentBp)}` : `${art} ${money(z.betragCents ?? 0)}`;
  }
  if (z.anteil) return z.anteil === "speisen" ? "Anteil Speisen" : "Anteil Getränke";
  return z.text || (z.art === "mehrverbrauch" ? "Mehrverbrauch" : "Leistung");
}

/** Wirkt ein Rabatt auf diese Betragszeile? Standardbezug: Rabatt darunter; Bezug „zeilen“: ausdrücklich gewählt. */
export function wirdRabattiert(zeilen: readonly AbrZeile[], index: number): boolean {
  const z = zeilen[index]!;
  if ((z.art !== "leistung" && z.art !== "mehrverbrauch") || (z.einzelpreisCents ?? 0) <= 0) return false;
  if (zeilen.some((r) => r.art === "rabatt" && r.bezug === "zeilen" && (r.bezugZeilen ?? []).includes(z.id))) return true;
  return z.art === "leistung" && zeilen.slice(index + 1).some((r) => r.art === "rabatt" && (r.bezug ?? "leistungen_darueber") === "leistungen_darueber");
}

export const alsEingabe = (z: AbrZeile): AbrZeileEingabe => ({
  id: z.id,
  art: z.art,
  text: z.text,
  beschreibung: z.beschreibung ?? null,
  mengeMilli: z.mengeMilli ?? null,
  einzelpreisCents: z.einzelpreisCents ?? null,
  steuersatzBp: z.steuersatzBp ?? null,
  einheit: z.einheit ?? null,
  proPerson: z.proPerson ?? false,
  prozentBp: z.prozentBp ?? null,
  betragCents: z.betragCents ?? null,
  bezug: z.bezug ?? null,
  bezugZeilen: z.bezug === "zeilen" ? (z.bezugZeilen ?? []) : null,
});

/**
 * Index für einen NEUEN Rabatt/Zuschlag: hinter dem führenden Block aus Leistungen und Rabatten, also vor
 * dem ersten Mehrverbrauch bzw. der ersten Leistung unter einem Rabatt (Standard „Rabatt vor
 * Mehrverbrauch“). Ein zweiter Rabatt landet damit unter dem ersten, nicht darüber.
 */
export function einfuegeIndexRabatt(zeilen: readonly AbrZeile[]): number {
  let rabattGesehen = false;
  for (let i = 0; i < zeilen.length; i++) {
    const z = zeilen[i]!;
    if (z.art === "rabatt" || z.art === "zuschlag") rabattGesehen = true;
    else if (z.art !== "leistung" || rabattGesehen) return i;
  }
  return zeilen.length;
}

/** Index, an dem „über dem Rabatt“ eingefügt wird: direkt vor dem ersten Rabatt, sonst am Ende der Leistungen. */
export function einfuegeIndex(zeilen: readonly AbrZeile[], wo: "ueber" | "unter"): number {
  const rabatt = zeilen.findIndex((z) => z.art === "rabatt");
  if (wo === "ueber") return rabatt >= 0 ? rabatt : zeilen.filter((z) => z.art === "leistung").length;
  return zeilen.length;
}

type Vorschau = { ergebnis: AbrechnungsErgebnis; fehler: null } | { ergebnis: null; fehler: string };

/** Vorschau für einen Drawer: dieselbe Rechenfunktion wie der Server, gespeichert wird erst nach „Übernehmen“. */
export function vorschau(zeilen: readonly (AbrZeileEingabe & { id?: string })[], gaeste: number): Vorschau {
  const rechen: AbrechnungsZeile[] = zeilen.map((z, i) => {
    const id = z.id ?? `neu-${i}`;
    if (z.art === "rabatt" || z.art === "zuschlag") return { id, art: z.art, prozentBp: z.prozentBp ?? null, betragCents: z.betragCents ?? null, bezug: z.bezug ?? "leistungen_darueber", bezugZeilen: z.bezugZeilen ?? null };
    const m = z.mengeMilli ?? 1000;
    return { id, art: z.art, mengeMilli: z.proPerson ? m * gaeste : m, einzelpreisCents: z.einzelpreisCents ?? undefined, steuersatzBp: z.steuersatzBp ?? undefined };
  });
  try {
    return { ergebnis: berechneAbrechnung(rechen), fehler: null };
  } catch (e) {
    return { ergebnis: null, fehler: e instanceof AbrechnungsFehler ? e.message : String(e) };
  }
}

/**
 * Hinweis nach dem Umordnen (Konzept § 7 „Reihenfolge-Verwirrung“): um wie viel ändert sich der
 * Rechnungsbetrag, und welche Rabatte/Zuschläge rechnen jetzt anders. `null`, wenn sich nichts ändert.
 * Beide Stände kommen vom Server; hier wird nur verglichen, nicht gerechnet.
 */
export function umordnungsHinweis(vorher: Pick<AbrAntwort, "zeilen" | "ergebnis">, nachher: Pick<AbrAntwort, "zeilen" | "ergebnis">): string | null {
  if (!vorher.ergebnis || !nachher.ergebnis) return null;
  const alt = new Map(vorher.ergebnis.zeilen.map((e) => [e.id, e.bruttoCents]));
  const teile: string[] = [];
  for (const z of nachher.zeilen) {
    if (z.art !== "rabatt" && z.art !== "zuschlag") continue;
    const a = alt.get(z.id);
    const n = nachher.ergebnis.zeilen.find((e) => e.id === z.id)?.bruttoCents;
    if (a != null && n != null && a !== n) teile.push(`${zeilenTitel(z)} jetzt ${money(Math.abs(n))} statt ${money(Math.abs(a))}`);
  }
  const diff = nachher.ergebnis.summeCents - vorher.ergebnis.summeCents;
  if (diff === 0 && teile.length === 0) return null;
  const kopf = diff === 0 ? "Rechnungsbetrag bleibt gleich" : `Betrag ändert sich um ${diff > 0 ? "+" : "−"}${money(Math.abs(diff))}: Rechnungsbetrag ${money(nachher.ergebnis.summeCents)} statt ${money(vorher.ergebnis.summeCents)}`;
  return [kopf, ...teile].join(". ") + ".";
}

/** Vorschau der Aufteilung einer Pauschale (Altfall), Anzeige im Hinweis vor „Aufteilung übernehmen“. */
export function anteilVorschau(preisProPersonCents: number, gaeste: number, getraenkeanteilBp: number) {
  const { speisenCents, getraenkeCents } = teileSpeisenGetraenke(preisProPersonCents, getraenkeanteilBp);
  const zeile = (cents: number, bp: number) => {
    const brutto = cents * gaeste;
    const ust = vatFromGross(brutto, bp);
    return { brutto, netto: brutto - ust, ust };
  };
  const speisen = zeile(speisenCents, 700);
  const getraenke = zeile(getraenkeCents, 1900);
  return { speisen, getraenke, gesamt: { brutto: speisen.brutto + getraenke.brutto, netto: speisen.netto + getraenke.netto, ust: speisen.ust + getraenke.ust } };
}

export type StatusTon = "neutral" | "warn" | "success" | "danger" | "info";

/** Pille im Kopf der Zahlungskarte. */
export function zahlungsPille(s: AbrAntwort): { text: string; ton: StatusTon } {
  const sal = s.saldo;
  if (!sal) return { text: "Noch nicht rechenbar", ton: "neutral" };
  if (sal.ueberzahlungCents > 0) {
    const u = s.abrechnung?.ueberzahlung;
    if (u) return u.entscheidung === "guthaben" ? { text: "Guthaben beim Kunden", ton: "info" } : { text: "Rückerstattung geplant", ton: "warn" };
    return { text: `Überzahlung ${money(sal.ueberzahlungCents)}`, ton: "warn" };
  }
  if (sal.bezahltCents === 0) return { text: "Keine Zahlung", ton: "neutral" };
  if (sal.offenCents > 0) return { text: `Offen ${money(sal.offenCents)}`, ton: "warn" };
  return { text: "Beglichen", ton: "success" };
}

/** „Zur Abrechnung“ springt ab dem Tag nach der Feier direkt in den Reiter „Abrechnen“ (Mockup, Einstieg). */
export function feierVorbei(eventDate: string | null | undefined, dateEnd: string | null | undefined, heute: string): boolean {
  const ende = (dateEnd ?? eventDate)?.slice(0, 10);
  return Boolean(ende) && ende! < heute;
}
